import React, { useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'
import OverflowText from 'components_new/molecules/OverflowText'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DataHealthSectionCard from 'components_new/organisms/internal/DataHealth/SectionCard'
import DataRerunProgressBar from 'components_new/organisms/internal/DataReruns/ProgressBar'

import { useDataRerunsActions } from 'redux/actions/internal/DataReruns'
import { useInternalDataReruns } from 'redux/hooks/internal/DataReruns'
import {
  DataRerunDetail,
  DataRerunSummary
} from 'redux/selectors/internal/DataReruns'
import { formatDayClock } from 'components_new/organisms/internal/DataRuns/utils'
import {
  formatRerunDuration,
  formatRerunProgress,
  rerunOutcome
} from 'components_new/organisms/internal/DataReruns/utils'

import DataRerunCustomerTable from './CustomerTable'

const HISTORY_COLUMNS = [
  { title: 'När', width: 120 },
  { title: 'Beställd av' },
  { title: 'Kunder', align: 'right' as const, width: 80 },
  { title: 'Utfall' },
  { title: 'Tid', align: 'right' as const, width: 100 }
]

const OUTCOME_SQUARE = {
  default: 'grey.400',
  error: 'error.main',
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main'
}

const FAILED_STATUSES = ['collect_failed', 'sim_failed']

export interface DataHealthRerunsTabProps {
  /** Shows Avbryt and Kör om fallerade on the top card. */
  canRerun: boolean
  /** Newest first, null until fetched. */
  reruns: DataRerunSummary[] | null
  rerunsFetching: boolean
  /** The rerun shown in the top card. */
  selectedRerun: DataRerunDetail | null
  selectedRerunFetching: boolean
  onSelectRerun: (rerunId: string) => void
  onRerunCancelled: () => void
  /** Called with the new rerun of the failed customers. */
  onFailedRetried: (rerunId: string) => void
}

/**
 * The Körningar tab: one run with its customers on top, every run
 * ordered so far below it.
 */
const DataHealthRerunsTab = (props: DataHealthRerunsTabProps) => {
  const {
    canRerun,
    onFailedRetried,
    onRerunCancelled,
    onSelectRerun,
    reruns,
    rerunsFetching,
    selectedRerun,
    selectedRerunFetching
  } = props

  const { cancelling, retrying } = useInternalDataReruns()
  const { tryCancelRerun, tryRetryFailedRerun } = useDataRerunsActions()

  const [confirm, setConfirm] = useState<'cancel' | 'retry' | null>(null)

  if (!reruns) {
    return rerunsFetching ? (
      <LoadingSection description={['Hämtar körningar']} sx={{ pt: 4 }} />
    ) : null
  }

  if (reruns.length === 0) {
    return <EmptyPlaceholder title="Inga körningar ännu." />
  }

  const outcome = selectedRerun ? rerunOutcome(selectedRerun) : null

  const failedCustomers = (selectedRerun?.customers ?? []).filter((customer) =>
    FAILED_STATUSES.includes(customer.status)
  )

  const canCancel = canRerun && selectedRerun?.status === 'running'
  const canRetry =
    canRerun &&
    selectedRerun !== null &&
    selectedRerun.finished_at !== null &&
    selectedRerun.failed_count > 0

  const cancel = () => {
    if (!selectedRerun) {
      return
    }

    tryCancelRerun(selectedRerun.id, (err) => {
      setConfirm(null)

      if (!err) {
        onRerunCancelled()
      }
    })
  }

  const retry = () => {
    if (!selectedRerun) {
      return
    }

    tryRetryFailedRerun(selectedRerun.id, (err, rerunId) => {
      setConfirm(null)

      if (!err && rerunId) {
        onFailedRetried(rerunId)
      }
    })
  }

  const topCardActions = canCancel ? (
    <Button
      onClick={() => setConfirm('cancel')}
      size="small"
      variant="outlined"
    >
      Avbryt
    </Button>
  ) : canRetry ? (
    <Button onClick={() => setConfirm('retry')} size="small" variant="outlined">
      {`Kör om fallerade (${selectedRerun.failed_count})`}
    </Button>
  ) : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {selectedRerun && outcome ? (
        <DataHealthSectionCard
          actions={topCardActions}
          meta={`${
            selectedRerun.trigger_type === 'schedule'
              ? 'Schemalagd'
              : `beställd av ${selectedRerun.started_by?.name ?? 'okänd'}`
          } · fönster ${selectedRerun.window_size}`}
          pill={{ label: outcome.label }}
          square={OUTCOME_SQUARE[outcome.color]}
          title={`Körning · ${formatDayClock(selectedRerun.started_at)}`}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {selectedRerun.description ? (
              <Text sx={{ whiteSpace: 'pre-line' }} variant="body2">
                {selectedRerun.description}
              </Text>
            ) : null}
            <Text
              sx={{
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums'
              }}
              variant="body2"
            >
              {formatRerunProgress(selectedRerun)}
            </Text>
            <DataRerunProgressBar rerun={selectedRerun} />
            <DataRerunCustomerTable customers={selectedRerun.customers} />
          </Box>
        </DataHealthSectionCard>
      ) : selectedRerunFetching ? (
        <LoadingSection description={['Hämtar körningen']} />
      ) : null}

      <DataHealthSectionCard
        meta="Klicka en rad för att visa körningen ovan"
        square="grey.400"
        title="Tidigare körningar"
      >
        <SimpleTable
          columns={HISTORY_COLUMNS}
          disableSorting
          rows={reruns.map((rerun) => {
            const rowOutcome = rerunOutcome(rerun)

            return {
              id: rerun.id,
              onClick: () => onSelectRerun(rerun.id),
              sx:
                rerun.id === selectedRerun?.id
                  ? { bgcolor: 'action.selected' }
                  : undefined,
              cells: [
                <Text
                  key="when"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  variant="body2"
                >
                  {formatDayClock(rerun.started_at)}
                </Text>,
                <Box key="by">
                  <Text variant="body2">
                    {rerun.trigger_type === 'schedule'
                      ? 'Schemalagd'
                      : rerun.started_by?.name ?? '–'}
                  </Text>
                  {rerun.description ? (
                    <OverflowText
                      color="text.secondary"
                      sx={{ maxWidth: 360 }}
                      variant="caption"
                    >
                      {rerun.description}
                    </OverflowText>
                  ) : null}
                </Box>,
                rerun.customer_count,
                <Box
                  key="outcome"
                  sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                >
                  <Tag color={rowOutcome.color} label={rowOutcome.label} />
                  <Text sx={{ color: 'text.secondary' }} variant="caption">
                    {formatRerunProgress(rerun)}
                  </Text>
                </Box>,
                formatRerunDuration(rerun)
              ]
            }
          })}
        />
      </DataHealthSectionCard>

      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setConfirm(null)} variant="text">
              Stäng
            </Button>
            <Button
              color="error"
              loading={cancelling}
              onClick={cancel}
              variant="contained"
            >
              Avbryt omkörningen
            </Button>
          </>
        }
        contentText={
          'Köade kunder startas inte. Kunder som kör nu kör klart, ' +
          'insamling och SIM avbryts inte.'
        }
        maxWidth="xs"
        onClose={() => setConfirm(null)}
        open={confirm === 'cancel'}
        title="Avbryt omkörningen?"
      />

      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setConfirm(null)} variant="text">
              Stäng
            </Button>
            <Button loading={retrying} onClick={retry} variant="contained">
              Starta omkörning
            </Button>
          </>
        }
        contentText={
          'En ny omkörning startas med samma insamlingsjobb och i samma ' +
          'ordning som förra gången.'
        }
        maxWidth="xs"
        onClose={() => setConfirm(null)}
        open={confirm === 'retry'}
        title={
          failedCustomers.length === 1
            ? 'Kör om 1 fallerad kund?'
            : `Kör om ${failedCustomers.length} fallerade kunder?`
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {failedCustomers.map((customer) => (
            <Text key={customer.id} variant="body2">
              {customer.customer_name}
            </Text>
          ))}
        </Box>
      </SimpleDialog>
    </Box>
  )
}

DataHealthRerunsTab.displayName = 'DataHealthRerunsTab'
export default DataHealthRerunsTab
